import React, { useEffect, useRef } from "react";
import type { Page, Story } from "../models/story";
import "./StorySpreadNavRail.css";

type Props = {
  story: Story;
  leftPage: Page;
  rightPage: Page;
  onSelectSpread: (spreadIndex: number) => void;
};

/**
 * Left rail: one thumbnail per reading page (cover, intros, each spread page,
 * back). Tapping a page jumps to the spread that contains it (two entries can
 * open the same spread).
 */
const StorySpreadNavRail = (props: Props) => {
  const { story, leftPage, rightPage, onSelectSpread } = props;
  const scrollTargetRef = useRef<HTMLButtonElement>(null);

  const pages = story.readingPages;
  const leftIdx = pages.indexOf(leftPage);
  const activeSpreadIndex =
    leftIdx < 0 ? 0 : Math.floor(leftIdx / 2);
  const scrollTargetPageIndex = activeSpreadIndex * 2;

  useEffect(() => {
    scrollTargetRef.current?.scrollIntoView({ block: "nearest" });
  }, [activeSpreadIndex]);

  if (pages.length <= 1) return null;

  return (
    <nav className="StorySpreadNavRail" aria-label="Jump to page by picture">
      {pages.map((page, i) => {
        const isVisible = page === leftPage || page === rightPage;
        const spreadIndex = Math.floor(i / 2);
        return (
          <button
            key={`${i}-${page.image}`}
            ref={i === scrollTargetPageIndex ? scrollTargetRef : undefined}
            type="button"
            className={
              "StorySpreadNavRail__btn" +
              (isVisible ? " StorySpreadNavRail__btn--active" : "")
            }
            aria-current={page === leftPage ? "true" : undefined}
            aria-label={`Open the spread with page ${i + 1} of ${pages.length}`}
            onClick={() => onSelectSpread(spreadIndex)}
          >
            <span className="StorySpreadNavRail__thumbWrap">
              <img
                className="StorySpreadNavRail__thumb"
                src={page.image}
                alt=""
              />
            </span>
          </button>
        );
      })}
    </nav>
  );
};

export default StorySpreadNavRail;
